/**
 * Poroboard's components (runebound-app `features/poroboard/builder`: TableBoard, BoardChrome, Inspector,
 * SequencePanel, Atmosphere), as shared primitives for every screen of Runebound Play. Keep them in step with the
 * editor: same sizes, borders, colours and motion.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, House } from 'lucide-react';
import { RetryArtwork } from '../CardImage';
import type { CardPlaceholderFace } from '../CardPlaceholder';
import { useCardArt } from '../card-art';
import { cn } from '../cn';
import { BAR_BORDER, GOLD_LINE, LOOK, PILL, PRIMARY, SIDE_LOOK, SURFACE, WELL, sideLine, type Side } from './look';

/* ------------------------------------------------------------------------------------------------------------------
 * Zones: the playmat's ruling (four corner brackets leaving a gap in the middle of every edge)
 * ---------------------------------------------------------------------------------------------------------------- */

/**
 * The mat's ruling: four corner brackets leaving a gap mid-edge. `side` draws it in that side's colour, otherwise the
 * shared gold; `soft` is an empty zone, `dim` one that takes no cards.
 */
export function Brackets({
  side,
  soft,
  dim,
  gx = 20,
  gy = 14,
  radius = 8,
}: {
  side?: Side | undefined;
  soft?: boolean | undefined;
  dim?: boolean | undefined;
  gx?: number;
  gy?: number;
  radius?: number;
}) {
  const c = side
    ? sideLine(side, dim ? 0.34 : soft ? 0.55 : 0.75)
    : dim
      ? GOLD_LINE.dim
      : soft
        ? GOLD_LINE.soft
        : GOLD_LINE.strong;
  const base: React.CSSProperties = {
    position: 'absolute',
    width: `calc(50% - ${gx / 2}px)`,
    height: `calc(50% - ${gy / 2}px)`,
    borderColor: c,
    borderStyle: 'solid',
    borderWidth: 0,
    pointerEvents: 'none',
  };
  return (
    <>
      <span
        aria-hidden
        style={{ ...base, left: 0, top: 0, borderLeftWidth: 1, borderTopWidth: 1, borderTopLeftRadius: radius }}
      />
      <span
        aria-hidden
        style={{ ...base, right: 0, top: 0, borderRightWidth: 1, borderTopWidth: 1, borderTopRightRadius: radius }}
      />
      <span
        aria-hidden
        style={{
          ...base,
          left: 0,
          bottom: 0,
          borderLeftWidth: 1,
          borderBottomWidth: 1,
          borderBottomLeftRadius: radius,
        }}
      />
      <span
        aria-hidden
        style={{
          ...base,
          right: 0,
          bottom: 0,
          borderRightWidth: 1,
          borderBottomWidth: 1,
          borderBottomRightRadius: radius,
        }}
      />
    </>
  );
}

/** A zone's name: small caps in its side's colour (gold when shared). */
export function ZoneLabel({
  side,
  children,
  inert,
  className,
  style,
}: {
  side?: Side | undefined;
  children: React.ReactNode;
  inert?: boolean | undefined;
  className?: string | undefined;
  style?: React.CSSProperties | undefined;
}) {
  return (
    <span
      className={cn(
        'pointer-events-none text-[10px] leading-3 font-semibold uppercase tracking-[0.12em] whitespace-pre-line',
        className,
      )}
      style={{ color: side ? sideLine(side, inert ? 0.6 : 0.95) : GOLD_LINE.label, ...style }}
    >
      {children}
    </span>
  );
}

/**
 * A ruled zone: brackets, its name top-left (or centred / left-centred when slim), and its content. Spreads any other
 * props (drop targets, data attributes) onto the section.
 */
export const Zone = React.forwardRef<
  HTMLElement,
  {
    side?: Side | undefined;
    label?: React.ReactNode;
    empty?: boolean | undefined;
    inert?: boolean | undefined;
    center?: boolean | undefined;
    slim?: boolean | undefined;
    over?: boolean | undefined;
    className?: string | undefined;
    style?: React.CSSProperties | undefined;
    children?: React.ReactNode;
  } & Omit<React.HTMLAttributes<HTMLElement>, 'className' | 'style' | 'children'>
>(function Zone({ side, label, empty, inert, center, slim, over, className, style, children, ...rest }, ref) {
  return (
    <section
      ref={ref}
      className={cn('relative min-w-0 min-h-0 rounded-[10px] transition-[background,box-shadow]', className)}
      style={{ ...style, ...(over ? DROP : {}) }}
      {...rest}
    >
      <Brackets side={side} soft={empty && !over} dim={inert} />
      {label ? (
        <ZoneLabel
          side={side}
          inert={inert}
          className={cn(
            'absolute',
            center
              ? 'inset-0 grid place-items-center text-center px-2'
              : slim
                ? 'left-3 top-1/2 -translate-y-1/2'
                : 'left-3 right-2 top-2',
          )}
        >
          {label}
        </ZoneLabel>
      ) : null}
      {children}
    </section>
  );
});

/** Something dragged over a drop target. */
export const DROP: React.CSSProperties = {
  background: 'rgba(139,92,246,0.12)',
  boxShadow: `inset 0 0 0 2px ${LOOK.violet}`,
};

/** The score track along a side's outer edge: 0 to max on one vertical line. `value` lights the reached pips. */
export function ScoreTrack({
  side,
  max = 8,
  value = 0,
  from = 'top',
}: {
  side: Side;
  max?: number;
  value?: number;
  from?: 'top' | 'bottom';
}) {
  const pips = Array.from({ length: max + 1 }, (_, i) => i);
  const order = from === 'bottom' ? [...pips].reverse() : pips;
  return (
    <div className="relative flex flex-col items-center gap-1.5" aria-label={`Score ${value} of ${max}`}>
      <span
        className="absolute top-0 bottom-0 left-1/2 w-px -translate-x-1/2"
        style={{ background: sideLine(side, 0.38) }}
      />
      {order.map((n) => {
        const on = n === value;
        return (
          <span
            key={n}
            className="relative shrink-0 w-6 h-6 rounded-full grid place-items-center text-[10.5px] font-semibold tabular-nums"
            style={{
              background: on ? sideLine(side, 0.9) : '#141b26',
              boxShadow: `inset 0 0 0 1px ${sideLine(side, on ? 1 : 0.5)}`,
              color: on ? '#0b0f14' : sideLine(side, 0.8),
            }}
          >
            {n}
          </span>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------------------------
 * The header bar
 * ---------------------------------------------------------------------------------------------------------------- */

/** A bordered home button (back to runebound.gg or the app's home). */
export function HomeButton({ href, label = 'Home' }: { href: string; label?: string }) {
  return (
    <a
      href={href}
      aria-label={label}
      title={label}
      className="w-[34px] h-[34px] inline-flex items-center justify-center rounded-[9px] shrink-0 transition-colors hover:bg-white/[0.06]"
      style={{ ...PILL, color: LOOK.sub }}
    >
      <House size={16} />
    </a>
  );
}

/**
 * The app's name in the header: an icon tile and the name with the brand's animated gradient on its first part
 * ("Poro"board, "Play").
 */
export function BrandTitle({
  icon,
  holo,
  rest = '',
  lead = '',
  as: As = 'span',
}: {
  icon: React.ReactNode;
  holo: string;
  rest?: string;
  /** Plain text before the gradient part ("Runebound " Play). */
  lead?: string;
  as?: 'h1' | 'span';
}) {
  return (
    <span className="inline-flex items-center gap-2 shrink-0">
      <span
        className="w-7 h-7 rounded-[8px] inline-flex items-center justify-center"
        style={{ background: 'rgba(124,92,255,0.16)', color: '#b9adff' }}
      >
        {icon}
      </span>
      <As className="font-display text-[15px] font-bold" style={{ color: LOOK.text }}>
        {lead}
        <span className="holo-text">{holo}</span>
        {rest}
      </As>
    </span>
  );
}

/** The 57px header bar: start (home, brand), a divider, the middle (toolbar), and the end (menus, account). */
export function AppBar({
  start,
  children,
  end,
  className,
}: {
  start: React.ReactNode;
  children?: React.ReactNode;
  end?: React.ReactNode;
  className?: string | undefined;
}) {
  return (
    <header
      className={cn('shrink-0 h-[57px] px-3 flex items-center gap-3 border-b', className)}
      style={{ borderColor: BAR_BORDER }}
    >
      {start}
      {children ? <span className="w-px h-6 mx-1 shrink-0" style={{ background: BAR_BORDER }} /> : null}
      <div className="flex-1 min-w-0 flex items-center gap-2.5">{children}</div>
      {end}
    </header>
  );
}

/** One button of a connected pill group (Undo | Redo | Clear). */
export type PillItem = {
  key: string;
  icon?: React.ReactNode;
  label: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
  active?: boolean;
};

/** Buttons joined into one pill with dividers (the editor's Undo / Redo / Clear). */
export function PillGroup({ items, label }: { items: PillItem[]; label?: string }) {
  return (
    <div
      className="inline-flex items-center rounded-[9px] overflow-hidden divide-x divide-[#293442] shrink-0"
      style={PILL}
      role="group"
      aria-label={label}
    >
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          onClick={it.onClick}
          disabled={it.disabled}
          title={it.title}
          aria-pressed={it.active}
          className="h-[34px] px-3 inline-flex items-center gap-1.5 text-[13px] font-medium whitespace-nowrap transition-colors enabled:hover:bg-white/[0.06] disabled:cursor-not-allowed"
          style={{
            color: it.disabled ? LOOK.faint : it.active ? '#fff' : LOOK.text,
            background: it.active ? 'rgba(117,70,255,0.22)' : undefined,
          }}
        >
          {it.icon}
          {it.label}
        </button>
      ))}
    </div>
  );
}

/** A rounded count or status chip on the bar ("13 / 50 cards", a timer). */
export function CountPill({
  icon,
  children,
  tone,
  title,
}: {
  icon?: React.ReactNode;
  children: React.ReactNode;
  tone?: string | undefined;
  title?: string | undefined;
}) {
  return (
    <span
      className="h-[34px] px-3 inline-flex items-center gap-2 rounded-full text-[13px] tabular-nums whitespace-nowrap shrink-0"
      style={{ ...PILL, color: tone ?? LOOK.text }}
      title={title}
    >
      {icon}
      {children}
    </span>
  );
}

/** A 34px dark button on the bar (a menu trigger: "Your boards ▾"). */
export const BarButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { caret?: boolean; open?: boolean }
>(function BarButton({ caret, open, className, children, style, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        'h-[34px] px-3 inline-flex items-center gap-2 rounded-[9px] text-[13px] font-medium whitespace-nowrap transition-colors hover:bg-white/[0.08] disabled:opacity-40 disabled:cursor-not-allowed',
        className,
      )}
      style={{ ...PILL, color: LOOK.text, ...style }}
      {...rest}
    >
      {children}
      {caret ? (
        <ChevronDown size={14} color={LOOK.sub} className={cn('transition-transform', open && 'rotate-180')} />
      ) : null}
    </button>
  );
});

/** The flat violet primary (Play, Start, Sign in) at the bar's size, or `big` on pages. */
export const PrimaryButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { big?: boolean }
>(function PrimaryButton({ big, className, style, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        big ? 'h-12 px-5 rounded-[10px] text-[15px] gap-2.5' : 'h-[34px] px-3.5 rounded-[9px] text-[13px] gap-2',
        'inline-flex items-center justify-center font-semibold transition-[filter] enabled:hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed',
        className,
      )}
      style={{ ...PRIMARY, ...style }}
      {...rest}
    />
  );
});

/** A secondary outlined button for pages ("What Pro includes"). */
export const GhostButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { big?: boolean }
>(function GhostButton({ big, className, style, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        big ? 'h-12 px-4 rounded-[10px] text-[14.5px] gap-2' : 'h-[34px] px-3 rounded-[9px] text-[13px] gap-1.5',
        'inline-flex items-center justify-center font-medium transition-colors hover:bg-white/[0.06] disabled:opacity-40 disabled:cursor-not-allowed',
        className,
      )}
      style={{
        boxShadow: `inset 0 0 0 1px ${LOOK.border}`,
        color: LOOK.text,
        background: 'rgba(12,18,26,0.6)',
        ...style,
      }}
      {...rest}
    />
  );
});

/* ------------------------------------------------------------------------------------------------------------------
 * The side panel
 * ---------------------------------------------------------------------------------------------------------------- */

/** The 352px right panel (card inspector over the sequence / chain / log). */
export function SidePanel({
  children,
  className,
  label,
}: {
  children: React.ReactNode;
  className?: string | undefined;
  label?: string;
}) {
  return (
    <aside
      className={cn('w-[352px] shrink-0 flex flex-col border-l min-h-0', className)}
      style={{ background: SURFACE, borderColor: BAR_BORDER }}
      aria-label={label}
    >
      {children}
    </aside>
  );
}

/**
 * A card's picture at the top of the panel: the top of the card, 203px tall; pointing at it (or `open`) lowers the
 * panel to show the whole card. Name and subtitle over the bottom, fading out when it opens.
 *
 * `image` takes the art's sources in order (one URL still works); each gets bounded retries before the next
 * (`useCardArt`, batch 3 #27). Without art, or once every source failed, the header is a compact band (name,
 * subtitle, `fallback`'s cost and Might, "Retry artwork" after a failure) instead of a blank 203px picture, so
 * the live numbers below stay in view.
 */
export function CardHeader({
  image,
  name,
  subtitle,
  landscape,
  open: forced,
  fallback,
}: {
  image: string | readonly string[] | null;
  name: React.ReactNode;
  subtitle?: React.ReactNode;
  landscape?: boolean | undefined;
  open?: boolean | undefined;
  /** Cost and Might printed in the compact band when there is no art. */
  fallback?: Pick<CardPlaceholderFace, 'cost' | 'might'> | undefined;
}) {
  const sources: readonly string[] = image === null ? [] : typeof image === 'string' ? [image] : image;
  const art = useCardArt(sources);
  const ref = useRef<HTMLDivElement>(null);
  const [full, setFull] = useState<number | null>(null);
  const [hover, setHover] = useState(false);
  const pictured = !art.failed && !art.none;
  const measure = () => {
    const w = ref.current?.clientWidth;
    if (w && pictured) setFull(Math.round(landscape ? w / CARD_RATIO : w * CARD_RATIO));
  };
  useEffect(() => {
    if (forced) measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forced]);
  if (!pictured) {
    const cost = fallback?.cost ?? null;
    const might = fallback?.might ?? null;
    return (
      <div
        className="relative shrink-0 flex items-start gap-3 px-4 py-3 border-b"
        style={{ background: LOOK.raised, borderColor: BAR_BORDER }}
        data-art={art.failed ? 'failed' : 'none'}
        data-testid="card-header"
      >
        {cost !== null ? (
          <span
            className="shrink-0 mt-0.5 size-8 rounded-full mono text-[15px] font-bold flex items-center justify-center"
            style={{ background: WELL, color: LOOK.text, boxShadow: `inset 0 0 0 1px ${LOOK.border}` }}
            aria-label={`Cost ${cost}`}
          >
            {cost}
          </span>
        ) : null}
        <div className="flex-1 min-w-0">
          <div className="text-[17px] leading-6 font-bold text-white truncate">{name}</div>
          {subtitle ? (
            <div className="text-[12.5px] leading-[18px] truncate" style={{ color: LOOK.sub }}>
              {subtitle}
            </div>
          ) : null}
          {art.failed ? (
            <div
              className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] leading-4"
              style={{ color: LOOK.muted }}
            >
              <span className="whitespace-nowrap">Artwork didn’t load</span>
              <RetryArtwork onRetry={art.retry} className="shrink-0" />
            </div>
          ) : null}
        </div>
        {might !== null ? (
          <span
            className="shrink-0 self-start mt-1 mono text-[20px] font-bold"
            style={{ color: '#a78bfa' }}
            title="Printed Might"
          >
            {might}
          </span>
        ) : null}
      </div>
    );
  }
  const whole = (hover || forced) && full !== null ? full : null;
  return (
    <div
      ref={ref}
      className="relative shrink-0 overflow-hidden transition-[height] duration-[380ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
      style={{ height: whole ?? 203, background: LOOK.raised }}
      data-art={art.loaded ? 'loaded' : 'loading'}
      data-testid="card-header"
      onPointerEnter={() => {
        measure();
        setHover(true);
      }}
      onPointerLeave={() => {
        setHover(false);
      }}
    >
      {art.src ? (
        <img
          key={art.tryKey}
          src={art.src}
          alt=""
          draggable={false}
          onLoad={art.onLoad}
          onError={art.onError}
          className={cn(
            'absolute inset-0 w-full h-full object-cover transition-opacity duration-300',
            art.loaded ? 'opacity-100' : 'opacity-0',
          )}
          style={{ objectPosition: landscape ? '50% 50%' : '50% 0%' }}
        />
      ) : null}
      {!art.loaded ? <div className="absolute inset-0 animate-pulse bg-panel3/60" /> : null}
      <div
        className={cn('absolute inset-0 transition-opacity duration-300', whole !== null && 'opacity-0')}
        style={{
          background: `linear-gradient(180deg, rgba(11,22,32,0) 45%, rgba(11,22,32,0.75) 72%, ${SURFACE} 100%)`,
        }}
      />
      <div className={cn('absolute inset-x-4 bottom-2 transition-opacity duration-200', whole !== null && 'opacity-0')}>
        <div
          className="text-[18px] leading-6 font-bold text-white truncate"
          style={{ textShadow: '0 2px 8px rgba(0,0,0,0.7)' }}
        >
          {name}
        </div>
        {subtitle ? (
          <div className="text-[12.5px] leading-[18px] truncate" style={{ color: LOOK.sub }}>
            {subtitle}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** A Riftbound card's height over its width. */
export const CARD_RATIO = 1039 / 744;

/**
 * Real tabs on a dark band across the panel: the chosen tab is cut from the panel below (same fill, no bottom edge).
 * `end` sits at the band's end (the card's actions).
 */
export function TabBand<T extends string>({
  tabs,
  value,
  onChange,
  end,
  label,
}: {
  tabs: readonly (readonly [T, string])[];
  value: T;
  onChange: (v: T) => void;
  end?: React.ReactNode;
  label?: string;
}) {
  return (
    <div className="flex items-end gap-3 px-3 pt-1.5 border-y" style={{ background: WELL, borderColor: LOOK.border }}>
      <div className="flex gap-0.5 -mb-px" role="tablist" aria-label={label}>
        {tabs.map(([v, l]) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={value === v}
            onClick={() => {
              onChange(v);
            }}
            className="h-[33px] px-4 rounded-t-[8px] border border-b-0 text-[13px] font-medium transition-colors hover:text-white"
            style={
              value === v
                ? { background: SURFACE, borderColor: LOOK.border, color: '#fff' }
                : { borderColor: 'transparent', color: LOOK.muted }
            }
          >
            {l}
          </button>
        ))}
      </div>
      <span className="flex-1" />
      {end ? <div className="self-center -mt-1.5 flex items-center gap-0.5">{end}</div> : null}
    </div>
  );
}

/** A 28px icon button for a band or panel (Duplicate, Remove). `danger` is red. */
export function IconAction({
  label,
  onClick,
  danger,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        'w-[28px] h-[28px] inline-flex items-center justify-center rounded-[6px] transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
        danger ? 'hover:bg-[rgba(255,64,92,0.12)]' : 'hover:bg-white/[0.06]',
      )}
      style={{ color: danger ? '#ff6b80' : LOOK.sub }}
    >
      {children}
    </button>
  );
}

/** A labelled field: its name small, above the control. */
export function Field({
  label,
  children,
  className,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  className?: string | undefined;
}) {
  return (
    <div className={cn('flex flex-col gap-1 min-w-0', className)}>
      <span className="text-[11.5px]" style={{ color: LOOK.muted }}>
        {label}
      </span>
      {children}
    </div>
  );
}

/** A two-or-more choice segmented control on a dark track (Ready / Exhausted, Tier 1 / Tier 2). */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: {
  options: readonly (readonly [T, React.ReactNode])[];
  value: T;
  onChange: (v: T) => void;
  label?: string;
  className?: string | undefined;
}) {
  return (
    <div
      className={cn('grid p-[3px] rounded-[8px]', className)}
      style={{
        gridTemplateColumns: `repeat(${options.length}, minmax(0,1fr))`,
        background: WELL,
        boxShadow: `inset 0 0 0 1px ${LOOK.border}`,
      }}
      role="radiogroup"
      aria-label={label}
    >
      {options.map(([v, l]) => (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={value === v}
          onClick={() => {
            onChange(v);
          }}
          className="h-[28px] px-2 rounded-[6px] text-[13px] font-medium transition-colors whitespace-nowrap"
          style={
            value === v
              ? { background: LOOK.raised, boxShadow: `inset 0 0 0 1px ${LOOK.violet}`, color: '#fff' }
              : { color: LOOK.muted }
          }
        >
          {l}
        </button>
      ))}
    </div>
  );
}

/** An on/off state as a pressable chip with a dot (Stunned, Hidden). */
export function ToggleChip({
  icon,
  label,
  on,
  onChange,
  disabled,
}: {
  icon?: React.ReactNode;
  label: React.ReactNode;
  on: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => {
        onChange(!on);
      }}
      disabled={disabled}
      className="h-[34px] px-3 inline-flex items-center gap-2 rounded-[8px] text-[13px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed enabled:hover:brightness-125"
      style={
        on
          ? { background: 'rgba(117,70,255,0.16)', boxShadow: `inset 0 0 0 1px ${LOOK.violet}`, color: '#fff' }
          : { background: WELL, boxShadow: `inset 0 0 0 1px ${LOOK.border}`, color: LOOK.sub }
      }
    >
      {icon}
      <span className="flex-1 text-left">{label}</span>
      <span
        className="w-2 h-2 rounded-full"
        style={{ background: on ? LOOK.violet : 'rgba(255,255,255,0.14)' }}
        aria-hidden
      />
    </button>
  );
}

/** One number in a stats strip, with a full-height up/down stepper when it can be changed. */
export type Stat = {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: string | undefined;
  title?: string | undefined;
  step?: { inc: () => void; dec: () => void; min?: boolean; max?: boolean; what: string };
};

/** Numbers side by side in one sunken strip (Might, Modifier, Damage). */
export function StatStrip({ stats, label }: { stats: Stat[]; label?: string }) {
  const btn =
    'flex-1 w-[30px] inline-flex items-center justify-center transition-colors hover:bg-white/[0.08] hover:text-white disabled:opacity-25 disabled:hover:bg-transparent';
  return (
    <div
      className="grid rounded-[8px] overflow-hidden divide-x divide-[#293442]"
      style={{
        gridTemplateColumns: `repeat(${stats.length}, minmax(0,1fr))`,
        background: WELL,
        boxShadow: `inset 0 0 0 1px ${LOOK.border}`,
      }}
      aria-label={label}
    >
      {stats.map((s) => (
        <div key={s.label} className="min-w-0 h-[46px] pl-2.5 flex items-stretch gap-1" title={s.title}>
          <div className="flex-1 min-w-0 flex flex-col justify-center">
            <span
              className="text-[10.5px] leading-[14px] font-medium uppercase tracking-[0.06em]"
              style={{ color: LOOK.muted }}
            >
              {s.label}
            </span>
            <span className="flex items-baseline gap-1.5 min-w-0">
              <span
                className="text-[17px] leading-[22px] font-bold tabular-nums"
                style={{ color: s.tone ?? LOOK.text }}
              >
                {s.value}
              </span>
              {s.sub ? (
                <span className="text-[11px] truncate tabular-nums" style={{ color: LOOK.muted }}>
                  {s.sub}
                </span>
              ) : null}
            </span>
          </div>
          {s.step ? (
            <span className="flex flex-col border-l" style={{ borderColor: LOOK.border }}>
              <button
                type="button"
                onClick={s.step.inc}
                disabled={s.step.max}
                className={cn(btn, 'border-b')}
                style={{ color: LOOK.sub, borderColor: LOOK.border }}
                aria-label={`More ${s.step.what}`}
              >
                <ChevronUp size={15} />
              </button>
              <button
                type="button"
                onClick={s.step.dec}
                disabled={s.step.min}
                className={btn}
                style={{ color: LOOK.sub }}
                aria-label={`Less ${s.step.what}`}
              >
                <ChevronDown size={15} />
              </button>
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** Tags under a card: bordered badges (type, domain with its icon, energy, Might). */
export function TagBadge({ icon, children }: { icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <span
      className={cn('h-[22px] px-2 inline-flex items-center gap-1 rounded-[5px] text-[11.5px]', icon ? 'pl-1.5' : null)}
      style={{ background: LOOK.raised, boxShadow: `inset 0 0 0 1px ${LOOK.border}`, color: LOOK.sub }}
    >
      {icon}
      {children}
    </span>
  );
}

/** Slides its content open and shut (its height animates from nothing to its own). */
export function Collapse({
  open,
  children,
  className,
}: {
  open: boolean;
  children: React.ReactNode;
  className?: string | undefined;
}) {
  return (
    <div
      className={cn(
        'grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
        open ? 'opacity-100' : 'opacity-0',
        className,
      )}
      style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
      inert={!open || undefined}
      aria-hidden={!open || undefined}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}

/**
 * A panel section with a header that folds it away and back (the sequence, the chain, the log): chevron, icon,
 * title, a badge at the end.
 */
export function FoldSection({
  icon,
  title,
  badge,
  children,
  defaultOpen = true,
  className,
}: {
  icon?: React.ReactNode;
  title: React.ReactNode;
  badge?: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  className?: string | undefined;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={cn('flex flex-col border-t', className)} style={{ borderColor: BAR_BORDER }}>
      <button
        type="button"
        onClick={() => {
          setOpen((o) => !o);
        }}
        aria-expanded={open}
        className="mx-2 my-1.5 px-2 py-1.5 flex items-center gap-2 rounded-[8px] text-left transition-colors hover:bg-white/[0.04]"
      >
        <ChevronDown
          size={15}
          color={LOOK.sub}
          className={cn('shrink-0 transition-transform duration-300', !open && '-rotate-90')}
        />
        {icon}
        <span className="text-[14px] font-semibold flex-1" style={{ color: LOOK.text }}>
          {title}
        </span>
        {badge}
      </button>
      <Collapse open={open}>
        <div className="px-4 pb-3">{children}</div>
      </Collapse>
    </section>
  );
}

/** A small status badge (Ready, Incomplete, Needs a fix; Tier 1). */
export function StatusBadge({
  color,
  icon,
  children,
}: {
  color: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <span
      className="h-[24px] px-2 inline-flex items-center gap-1 rounded-[6px] text-[12px] font-medium whitespace-nowrap"
      style={{ color, background: `${color}14`, boxShadow: `inset 0 0 0 1px ${color}55` }}
    >
      {icon}
      {children}
    </span>
  );
}

/** A dropdown or modal surface (menus, sheets): the panel fill, a hairline and a deep shadow. */
export const FLOAT: React.CSSProperties = {
  background: SURFACE,
  boxShadow: `inset 0 0 0 1px ${LOOK.border}, 0 18px 50px rgba(0,0,0,0.6)`,
};

/* ------------------------------------------------------------------------------------------------------------------
 * Pages (landing, setup forms)
 * ---------------------------------------------------------------------------------------------------------------- */

/** The landing hero: an eyebrow chip, a big headline, the pitch and the way in, over banner art fading into the page. */
export function Hero({
  eyebrow,
  eyebrowIcon,
  title,
  children,
  art,
  actions,
}: {
  eyebrow: React.ReactNode;
  eyebrowIcon?: React.ReactNode;
  title: React.ReactNode;
  children?: React.ReactNode;
  art?: string | null;
  actions?: React.ReactNode;
}) {
  return (
    <section className="relative overflow-hidden -mx-8 px-8 pt-14 pb-10">
      {art ? (
        <img
          src={art}
          alt=""
          aria-hidden
          className="absolute inset-y-0 right-0 w-[60%] h-full object-cover opacity-80 pointer-events-none"
          style={{ objectPosition: '55% 14%' }}
        />
      ) : null}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `linear-gradient(90deg, ${LOOK.page} 0%, ${LOOK.page} 42%, rgba(12,16,21,0.7) 62%, rgba(12,16,21,0.35) 100%), linear-gradient(180deg, transparent 60%, ${LOOK.page} 100%), linear-gradient(270deg, ${LOOK.page} 0%, transparent 18%)`,
        }}
      />
      <div className="relative max-w-[640px]">
        <div
          className="inline-flex items-center gap-2 h-[26px] pl-1.5 pr-2.5 rounded-full text-[11.5px] font-semibold tracking-[0.08em] uppercase"
          style={{
            background: 'rgba(124,92,255,0.14)',
            boxShadow: 'inset 0 0 0 1px rgba(124,92,255,0.35)',
            color: '#c3b8ff',
          }}
        >
          <span
            className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center"
            style={{ background: 'rgba(124,92,255,0.3)' }}
          >
            {eyebrowIcon}
          </span>
          {eyebrow}
        </div>
        <h1 className="mt-4 font-display text-[46px] leading-[1.04] font-bold tracking-[-0.02em] text-white text-balance">
          {title}
        </h1>
        {children ? (
          <div className="mt-4 text-[16px] leading-[25px] max-w-[560px]" style={{ color: '#D5DCE6' }}>
            {children}
          </div>
        ) : null}
        {actions ? <div className="mt-7">{actions}</div> : null}
      </div>
    </section>
  );
}

/** A step or feature card on a page (icon tile, a small kicker, the title, its text). */
export function FeatureCard({
  icon,
  kicker,
  title,
  children,
}: {
  icon: React.ReactNode;
  kicker?: React.ReactNode;
  title: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div
      className="rounded-[12px] p-5 flex flex-col gap-3"
      style={{ background: SURFACE, boxShadow: `inset 0 0 0 1px ${BAR_BORDER}` }}
    >
      <span className="flex items-center gap-2.5">
        <span
          className="w-9 h-9 rounded-[9px] inline-flex items-center justify-center"
          style={{ background: 'rgba(124,92,255,0.14)', color: '#b9adff' }}
        >
          {icon}
        </span>
        {kicker ? (
          <span className="text-[12px] font-semibold tabular-nums" style={{ color: LOOK.muted }}>
            {kicker}
          </span>
        ) : null}
      </span>
      <span className="text-[15px] font-semibold" style={{ color: LOOK.text }}>
        {title}
      </span>
      {children ? (
        <span className="text-[13px] leading-[19px]" style={{ color: LOOK.sub }}>
          {children}
        </span>
      ) : null}
    </div>
  );
}

/** A framed "window" on a page (a form, a preview): a 44px caption bar and its body. */
export function Frame({
  title,
  icon,
  end,
  children,
  className,
}: {
  title: React.ReactNode;
  icon?: React.ReactNode;
  end?: React.ReactNode;
  children: React.ReactNode;
  className?: string | undefined;
}) {
  return (
    <section
      className={cn('rounded-[16px] overflow-hidden', className)}
      style={{ background: SURFACE, boxShadow: `inset 0 0 0 1px ${LOOK.border}, 0 30px 80px rgba(0,0,0,0.35)` }}
    >
      <header className="h-11 px-4 flex items-center gap-3 border-b" style={{ borderColor: BAR_BORDER }}>
        {icon}
        <span className="text-[13px] font-semibold" style={{ color: LOOK.text }}>
          {title}
        </span>
        <span className="flex-1" />
        {end}
      </header>
      {children}
    </section>
  );
}

/** A text input or select in the panel style. */
export const inputClass =
  'w-full h-[34px] px-3 rounded-[6px] text-[13px] outline-none transition-colors focus:shadow-[inset_0_0_0_1px_#8b5cf6]';
export const inputStyle: React.CSSProperties = {
  background: LOOK.raised,
  boxShadow: `inset 0 0 0 1px ${LOOK.border}`,
  color: LOOK.text,
};

/* ------------------------------------------------------------------------------------------------------------------
 * Pages (lobby, settings): links that look like the buttons, a page heading, an on/off switch, a menu
 * ---------------------------------------------------------------------------------------------------------------- */

/** The classes of a primary or ghost control at the bar's size, or `big` on pages (for links styled as buttons). */
export function buttonLook(kind: 'primary' | 'ghost', big?: boolean): string {
  return cn(
    big
      ? kind === 'primary'
        ? 'h-12 px-5 rounded-[10px] text-[15px] gap-2.5'
        : 'h-12 px-4 rounded-[10px] text-[14.5px] gap-2'
      : kind === 'primary'
        ? 'h-[34px] px-3.5 rounded-[9px] text-[13px] gap-2'
        : 'h-[34px] px-3 rounded-[9px] text-[13px] gap-1.5',
    'inline-flex items-center justify-center whitespace-nowrap',
    kind === 'primary'
      ? 'font-semibold transition-[filter] hover:brightness-110'
      : 'font-medium transition-colors hover:bg-white/[0.06]',
  );
}

/** The ghost button's fill and edge. */
export const GHOST: React.CSSProperties = {
  boxShadow: `inset 0 0 0 1px ${LOOK.border}`,
  color: LOOK.text,
  background: 'rgba(12,18,26,0.6)',
};

/** A link that looks like `PrimaryButton` ("Sign in to start", "Go to Play"). */
export const PrimaryLink = React.forwardRef<
  HTMLAnchorElement,
  React.AnchorHTMLAttributes<HTMLAnchorElement> & { big?: boolean }
>(function PrimaryLink({ big, className, style, ...rest }, ref) {
  return (
    <a ref={ref} className={cn(buttonLook('primary', big), className)} style={{ ...PRIMARY, ...style }} {...rest} />
  );
});

/** A link that looks like `GhostButton` ("Build a deck", "Open in Runebound"). */
export const GhostLink = React.forwardRef<
  HTMLAnchorElement,
  React.AnchorHTMLAttributes<HTMLAnchorElement> & { big?: boolean }
>(function GhostLink({ big, className, style, ...rest }, ref) {
  return <a ref={ref} className={cn(buttonLook('ghost', big), className)} style={{ ...GHOST, ...style }} {...rest} />;
});

/** A page's heading row: an optional eyebrow chip, the title, a line under it, and controls at the end. */
export function PageHead({
  title,
  icon,
  children,
  end,
  badge,
}: {
  title: React.ReactNode;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  end?: React.ReactNode;
  badge?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end gap-x-6 gap-y-4 pt-10 pb-6">
      <div className="flex-1 min-w-[260px] flex flex-col gap-2">
        <div className="flex items-center gap-3">
          {icon ? (
            <span
              className="w-9 h-9 rounded-[9px] inline-flex items-center justify-center shrink-0"
              style={{ background: 'rgba(124,92,255,0.14)', color: '#b9adff' }}
            >
              {icon}
            </span>
          ) : null}
          <h1
            className="font-display text-[30px] leading-[36px] font-bold tracking-[-0.015em]"
            style={{ color: LOOK.text }}
          >
            {title}
          </h1>
          {badge}
        </div>
        {children ? (
          <div className="text-[14px] leading-[21px] max-w-[640px]" style={{ color: LOOK.sub }}>
            {children}
          </div>
        ) : null}
      </div>
      {end}
    </div>
  );
}

/** An on/off switch in the editor's violet (a setting row's control). Renders a real checkbox with `role="switch"`. */
export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: string;
  disabled?: boolean;
}) {
  return (
    <span className="relative inline-flex shrink-0">
      <input
        type="checkbox"
        role="switch"
        aria-label={label}
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
      />
      <span
        aria-hidden
        className="relative w-[38px] h-[22px] rounded-full transition-colors peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[#8b5cf6]"
        style={
          checked
            ? { background: 'rgba(117,70,255,0.55)', boxShadow: `inset 0 0 0 1px ${LOOK.violet}` }
            : { background: WELL, boxShadow: `inset 0 0 0 1px ${LOOK.border}` }
        }
      >
        <span
          className="absolute top-[3px] w-4 h-4 rounded-full transition-[left,background]"
          style={{ left: checked ? 19 : 3, background: checked ? '#fff' : LOOK.muted }}
        />
      </span>
    </span>
  );
}

/** A dropdown menu's item (account menu): 36px, an icon and the label. */
export const MENU_ITEM =
  'w-full h-9 px-2.5 flex items-center gap-2.5 rounded-[7px] text-[13px] text-left transition-colors hover:bg-white/[0.06]';

/** A message strip on a page or in a panel (an error, a notice, the queue). */
export function Notice({
  tone = 'info',
  icon,
  children,
  className,
  ...rest
}: {
  tone?: 'info' | 'warn' | 'bad' | 'ok';
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string | undefined;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'className' | 'children'>) {
  const c = { info: LOOK.violet, warn: LOOK.warn, bad: LOOK.bad, ok: LOOK.ok }[tone];
  return (
    <div
      className={cn('rounded-[10px] px-3 py-2.5 flex items-start gap-2.5 text-[13px] leading-[19px]', className)}
      style={{ background: `${c}14`, boxShadow: `inset 0 0 0 1px ${c}4d`, color: LOOK.text }}
      {...rest}
    >
      {icon ? (
        <span className="shrink-0 mt-px" style={{ color: c }}>
          {icon}
        </span>
      ) : null}
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------------------------
 * The match table (Runebound Play's board): buttons, prompts, dialogs and panel pieces in the editor's look
 * ---------------------------------------------------------------------------------------------------------------- */

/** The variants of `ActionButton` (the old `Button`'s names, so prompts keep their wording). */
export type ActionVariant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger';

/**
 * A button in the editor's look with the props of the old `Button` (variant, size, full, icon): `primary` is the
 * flat violet, `danger` a red outline, every other variant the ghost. `sm` is 30px, `md` the bar's 34px, `lg` 44px.
 */
export const ActionButton = React.forwardRef<
  HTMLButtonElement,
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
    variant?: ActionVariant | undefined;
    size?: 'sm' | 'md' | 'lg' | undefined;
    full?: boolean | undefined;
    icon?: React.ReactNode;
    children?: React.ReactNode;
  }
>(function ActionButton({ variant = 'primary', size = 'md', full, icon, className, style, children, ...rest }, ref) {
  const primary = variant === 'primary';
  const danger = variant === 'danger';
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        size === 'sm'
          ? 'h-[30px] px-2.5 rounded-[8px] text-[12.5px] gap-1.5'
          : size === 'lg'
            ? 'h-11 px-4 rounded-[10px] text-[14px] gap-2'
            : 'h-[34px] px-3 rounded-[9px] text-[13px] gap-2',
        'inline-flex items-center justify-center whitespace-nowrap select-none disabled:opacity-45 disabled:cursor-not-allowed',
        primary
          ? 'font-semibold transition-[filter] enabled:hover:brightness-110'
          : 'font-medium transition-colors enabled:hover:bg-white/[0.06]',
        full && 'w-full',
        className,
      )}
      style={{
        ...(primary
          ? PRIMARY
          : danger
            ? { boxShadow: `inset 0 0 0 1px ${LOOK.bad}66`, color: LOOK.bad, background: 'rgba(255,92,122,0.06)' }
            : { boxShadow: `inset 0 0 0 1px ${LOOK.border}`, color: LOOK.text, background: 'rgba(12,18,26,0.6)' }),
        ...style,
      }}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
});

/** A key's name inside a button or a list ("Space" on End turn). */
export function KeyHint({ children, onPrimary }: { children: React.ReactNode; onPrimary?: boolean }) {
  return (
    <kbd
      className="h-[18px] px-1.5 inline-flex items-center rounded-[5px] font-sans text-[10.5px] font-medium tabular-nums"
      style={
        onPrimary
          ? { background: 'rgba(255,255,255,0.16)', color: '#fff' }
          : { background: WELL, boxShadow: `inset 0 0 0 1px ${LOOK.border}`, color: LOOK.sub }
      }
    >
      {children}
    </kbd>
  );
}

/** A side's name with its dot ("● You", "● Opponent"), in its colour. */
export function SideTag({
  side,
  children,
  className,
}: {
  side: Side;
  children: React.ReactNode;
  className?: string | undefined;
}) {
  return (
    <span
      className={cn('inline-flex items-center gap-1.5 text-[11px] whitespace-nowrap', className)}
      style={{ color: SIDE_LOOK[side].text }}
    >
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: SIDE_LOOK[side].hue }} />
      {children}
    </span>
  );
}

/**
 * A `FoldSection` whose body fills the height left in the panel (the log): give it `flex-1 min-h-0` and put a
 * scrolling `h-full` list inside.
 */
export function FillFoldSection({
  icon,
  title,
  badge,
  children,
  defaultOpen = true,
  className,
  bodyClassName,
}: {
  icon?: React.ReactNode;
  title: React.ReactNode;
  badge?: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  className?: string | undefined;
  bodyClassName?: string | undefined;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section
      className={cn('flex flex-col border-t min-h-0', open && 'flex-1', className)}
      style={{ borderColor: BAR_BORDER }}
    >
      <button
        type="button"
        onClick={() => {
          setOpen((o) => !o);
        }}
        aria-expanded={open}
        className="shrink-0 mx-2 my-1.5 px-2 py-1.5 flex items-center gap-2 rounded-[8px] text-left transition-colors hover:bg-white/[0.04]"
      >
        <ChevronDown
          size={15}
          color={LOOK.sub}
          className={cn('shrink-0 transition-transform duration-300', !open && '-rotate-90')}
        />
        {icon}
        <span className="text-[14px] font-semibold flex-1" style={{ color: LOOK.text }}>
          {title}
        </span>
        {badge}
      </button>
      <Collapse open={open} className={cn(open && 'flex-1 min-h-0')}>
        <div className={cn('h-full min-h-0', bodyClassName)}>{children}</div>
      </Collapse>
    </section>
  );
}

/** The side panel's empty state: an icon tile, a title and a line ("No card selected"). */
export function PanelEmpty({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5 px-4 pt-4 pb-4">
      <div className="flex items-center gap-2.5 text-[14px] font-semibold" style={{ color: LOOK.text }}>
        <span
          className="w-8 h-8 rounded-[7px] inline-flex items-center justify-center shrink-0"
          style={{ background: LOOK.raisedHi, boxShadow: `inset 0 0 0 1px ${LOOK.border}`, color: LOOK.sub }}
        >
          {icon}
        </span>
        {title}
      </div>
      {children ? (
        <p className="text-[13px] leading-[19px]" style={{ color: LOOK.sub }}>
          {children}
        </p>
      ) : null}
    </div>
  );
}

/** A floating surface over the table (a prompt, a toast, a waiting notice): the `FLOAT` fill, 12px corners. */
export function FloatCard({
  children,
  className,
  style,
  ...rest
}: {
  children: React.ReactNode;
  className?: string | undefined;
  style?: React.CSSProperties | undefined;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'className' | 'style' | 'children'>) {
  return (
    <div className={cn('rounded-[12px]', className)} style={{ ...FLOAT, color: LOOK.text, ...style }} {...rest}>
      {children}
    </div>
  );
}

/**
 * A modal over the table (mulligan, game over): a dimmed backdrop and a `FLOAT` card with a title, a line under it
 * and the body. Spreads other props (test ids) onto the dialog.
 */
export function ModalFrame({
  title,
  subtitle,
  icon,
  children,
  width = 760,
  className,
  ...rest
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  children: React.ReactNode;
  width?: number;
  className?: string | undefined;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'className' | 'children' | 'title'>) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(6,9,13,0.72)', backdropFilter: 'blur(4px)' }}
      role="dialog"
      aria-modal="true"
      {...rest}
    >
      <div
        className={cn(
          'w-full max-h-[calc(100dvh-32px)] overflow-y-auto rounded-[14px] p-5 flex flex-col gap-4',
          className,
        )}
        style={{ ...FLOAT, maxWidth: width, color: LOOK.text }}
      >
        <div className="flex items-start gap-3">
          {icon ? (
            <span
              className="w-10 h-10 rounded-[10px] inline-flex items-center justify-center shrink-0"
              style={{ background: 'rgba(124,92,255,0.14)', color: '#b9adff' }}
            >
              {icon}
            </span>
          ) : null}
          <div className="min-w-0">
            <h2 className="font-display text-[20px] leading-[26px] font-bold" style={{ color: LOOK.text }}>
              {title}
            </h2>
            {subtitle ? (
              <p className="mt-0.5 text-[13px] leading-[19px]" style={{ color: LOOK.sub }}>
                {subtitle}
              </p>
            ) : null}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

/** A small caps heading inside a panel or menu ("MOVE", "COUNTERS"). */
export function SectionLabel({ children, className }: { children: React.ReactNode; className?: string | undefined }) {
  return (
    <span
      className={cn('text-[10.5px] font-semibold uppercase tracking-[0.1em]', className)}
      style={{ color: LOOK.muted }}
    >
      {children}
    </span>
  );
}

/**
 * A pressable choice in a prompt (a payment source, a mode) in the editor's look, with the old `Chip`'s props:
 * `selected` lights it violet, `tint` colours a selected chip (a rune's domain), `small` is 28px.
 */
export function ChoiceChip({
  selected,
  tint,
  icon,
  small,
  className,
  children,
  style,
  ...rest
}: {
  selected?: boolean | undefined;
  tint?: string | undefined;
  icon?: React.ReactNode;
  small?: boolean | undefined;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const on = selected ? (tint ?? LOOK.violet) : null;
  return (
    <button
      type="button"
      aria-pressed={selected}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-[8px] whitespace-nowrap font-medium transition-[background,box-shadow] select-none enabled:hover:brightness-125 disabled:opacity-40 disabled:cursor-not-allowed',
        small ? 'h-7 px-2.5 text-[12px]' : 'h-[34px] px-3 text-[13px]',
        className,
      )}
      style={{
        ...(on
          ? { background: `${on}26`, boxShadow: `inset 0 0 0 1px ${on}`, color: tint ?? '#fff' }
          : { background: WELL, boxShadow: `inset 0 0 0 1px ${LOOK.border}`, color: LOOK.sub }),
        ...style,
      }}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}
